import { useEffect, useMemo, useState } from "react";
import { Plus, Search, Pencil, Trash2, Carrot } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, EmptyState } from "@/components/Logo";
import DuplicatesPanel from "@/components/ingredients/DuplicatesPanel";
import IngredientDialog from "@/components/ingredients/IngredientDialog";
import { ChangeBadge } from "@/components/invoices/InvoiceDetailDialog";
import api, { errMsg } from "@/lib/api";
import { eurUnit, dateFr, fold, unitLabel, catLabel } from "@/lib/format";
import { t } from "@/i18n";

export default function Ingredients() {
  const [items, setItems] = useState([]);
  const [dups, setDups] = useState([]);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState({ open: false, ingredient: null });

  const load = () => {
    api.get("/ingredients").then(({ data }) => setItems(data));
    api.get("/duplicates").then(({ data }) => setDups(data));
  };
  useEffect(load, []);

  const filtered = useMemo(() => items.filter((i) => fold(`${i.name} ${catLabel(i.category)}`).includes(fold(q))), [items, q]);

  const remove = async (ing) => {
    if (!window.confirm(t("ingredients.supprimer_x", { v0: ing.name }))) return;
    try {
      await api.delete(`/ingredients/${ing.id}`);
      toast.success(t("ingredients.ingredient_supprime"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <>
      <PageHeader eyebrow={t("ingredients.base_de_couts")} title={t("common.ingredients")} description={t("ingredients.prix_d_achat_alimentes_par_vos_factures_ch")}
        actions={<Button onClick={() => setDialog({ open: true, ingredient: null })} data-testid="add-ingredient-btn"><Plus className="mr-2 h-4 w-4" />{t("common.ajouter")}</Button>} />
      <DuplicatesPanel items={dups} reload={load} />
      <div className="relative mb-4 md:max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder={t("ingredients.rechercher_un_ingredient")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="ingredients-search-input" />
      </div>
      {items.length === 0 ? (
        <EmptyState icon={Carrot} title={t("ingredients.aucun_ingredient")} description={t("ingredients.importez_une_facture_ou_ajoutez_vos_ingred")} testId="ingredients-empty" />
      ) : (
        <>
        <div className="space-y-2 md:hidden" data-testid="ingredients-mobile-list">
          {filtered.map((i) => (
            <button key={i.id} type="button" onClick={() => setDialog({ open: true, ingredient: i })}
              className="flex w-full items-center gap-3 rounded-xl border bg-card p-3.5 text-left active:bg-muted/50" data-testid={`ingredient-card-${i.id}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{i.name}{i.estimated && <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400">{t("common.estime")}</span>}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{catLabel(i.category)} · {t("common.n_recipes_paren", { v0: i.recipes_count })}</p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <p className="num text-sm font-semibold">{eurUnit(i.current_unit_cost)}<span className="font-normal text-muted-foreground">/{unitLabel(i.unit)}</span></p>
                <ChangeBadge value={i.price_history.length > 1 ? i.change_pct_30d : null} />
              </div>
            </button>
          ))}
        </div>
        <div className="hidden overflow-x-auto rounded-xl border bg-card md:block">
          <Table data-testid="ingredients-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.ingredient")}</TableHead>
                <TableHead className="hidden md:table-cell">{t("common.categorie")}</TableHead>
                <TableHead className="text-right">{t("ingredients.prix_actuel")}</TableHead>
                <TableHead className="hidden text-right md:table-cell">{t("common.prix_moyen")}</TableHead>
                <TableHead className="text-right">{t("ingredients.var_30_j")}</TableHead>
                <TableHead className="hidden text-right lg:table-cell">{t("ingredients.var_totale")}</TableHead>
                <TableHead className="hidden text-right sm:table-cell">{t("common.recettes")}</TableHead>
                <TableHead className="hidden lg:table-cell">{t("ingredients.mis_a_jour")}</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((i) => (
                <TableRow key={i.id} className="cursor-pointer" onClick={() => setDialog({ open: true, ingredient: i })} data-testid={`ingredient-row-${i.id}`}>
                  <TableCell className="font-medium">
                    {i.name}
                    {i.estimated && <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400">{t("common.estime")}</span>}
                  </TableCell>
                  <TableCell className="hidden text-muted-foreground md:table-cell">{catLabel(i.category)}</TableCell>
                  <TableCell className="num text-right">{eurUnit(i.current_unit_cost)}<span className="text-muted-foreground">/{unitLabel(i.unit)}</span></TableCell>
                  <TableCell className="num hidden text-right text-muted-foreground md:table-cell">{eurUnit(i.average_unit_cost)}</TableCell>
                  <TableCell className="text-right"><ChangeBadge value={i.price_history.length > 1 ? i.change_pct_30d : null} /></TableCell>
                  <TableCell className="hidden text-right lg:table-cell"><ChangeBadge value={i.price_history.length > 1 ? i.change_pct_total : null} /></TableCell>
                  <TableCell className="num hidden text-right sm:table-cell">{i.recipes_count}</TableCell>
                  <TableCell className="hidden text-xs text-muted-foreground lg:table-cell">{dateFr(i.last_updated)}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" onClick={() => setDialog({ open: true, ingredient: i })} data-testid={`ingredient-edit-btn-${i.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(i)} data-testid={`ingredient-delete-btn-${i.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        </>
      )}
      <IngredientDialog open={dialog.open} ingredient={dialog.ingredient} onClose={() => setDialog({ open: false, ingredient: null })} onSaved={load} />
    </>
  );
}
